import { defaultStaticRanges } from 'react-date-range';

/**
 * Formats a Date object into a string representation.
 * Default format: MM/DD/YYYY
 */
export const formatDateString = (dateInput, outputFormat = 'MM/DD/YYYY') => {
  if (!dateInput) return '';
  const dateInstance = new Date(dateInput);
  if (isNaN(dateInstance.getTime())) return String(dateInput);

  const year = dateInstance.getFullYear();
  const month = String(dateInstance.getMonth() + 1).padStart(2, '0');
  const day = String(dateInstance.getDate()).padStart(2, '0');

  if (outputFormat === 'YYYY-MM-DD') {
    return `${year}-${month}-${day}`;
  }
  return `${month}/${day}/${year}`;
};

/**
 * Calculates date range for 2 weeks ahead of current week (starting Sunday 2 weeks ahead).
 */
export const getTwoWeeksAheadRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday

  const currentWeekSunday = new Date(today);
  currentWeekSunday.setDate(today.getDate() - dayOfWeek);
  currentWeekSunday.setHours(0, 0, 0, 0);

  const twoWeeksAheadSunday = new Date(currentWeekSunday);
  twoWeeksAheadSunday.setDate(currentWeekSunday.getDate() + 14);

  const twoWeeksAheadSaturday = new Date(twoWeeksAheadSunday);
  twoWeeksAheadSaturday.setDate(twoWeeksAheadSunday.getDate() + 6);

  return {
    startDate: twoWeeksAheadSunday,
    endDate: twoWeeksAheadSaturday,
    startStr: formatDateString(twoWeeksAheadSunday),
    endStr: formatDateString(twoWeeksAheadSaturday),
    rangeString: `${formatDateString(twoWeeksAheadSunday)} - ${formatDateString(twoWeeksAheadSaturday)}`,
  };
};

/**
 * Parses a range string ("MM/DD/YYYY - MM/DD/YYYY") into Date objects.
 */
export const parseRangeString = (rangeString) => {
  if (!rangeString || typeof rangeString !== 'string' || !rangeString.includes(' - ')) {
    return null;
  }
  const [startStr, endStr] = rangeString.split(' - ');
  const startDate = new Date(startStr);
  const endDate = new Date(endStr);
  if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
    return { startDate, endDate };
  }
  return null;
};

/**
 * Computes initial start and end dates based on input props.
 */
export const computeInitialDates = ({ startDate, endDate, value, defaultToPastWeek, minDateThreshold, customMinDate }) => {
  let start, end;
  if (startDate && endDate) {
    start = new Date(startDate);
    end = new Date(endDate);
  } else {
    const parsedRange = parseRangeString(value);
    if (parsedRange) {
      start = parsedRange.startDate;
      end = parsedRange.endDate;
    } else if (defaultToPastWeek) {
      const today = new Date();
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(today.getDate() - 7);
      start = oneWeekAgo;
      end = today;
    } else {
      const today = new Date();
      start = today;
      end = today;
    }
  }

  const activeMinThreshold = minDateThreshold || customMinDate;
  const isValidMinThreshold = activeMinThreshold instanceof Date && !isNaN(activeMinThreshold.getTime());
  if (isValidMinThreshold && start < activeMinThreshold) {
    start = new Date(activeMinThreshold);
  }

  return { start, end };
};

/**
 * Computes minDate and maxDate limits from props.
 */
export const computeMinMaxDates = ({ disablePastDates, disableFutureDates, customMinDate, customMaxDate, minDateThreshold, maxDateThreshold }) => {
  let minDate = minDateThreshold || customMinDate;
  if (disablePastDates && !minDate) {
    const todayAtMidnight = new Date();
    todayAtMidnight.setHours(0, 0, 0, 0);
    minDate = todayAtMidnight;
  }

  let maxDate = maxDateThreshold || customMaxDate;
  if (disableFutureDates && !maxDate) {
    maxDate = new Date();
  }

  return { minDate, maxDate };
};

/**
 * Computes static preset ranges (filters out "This Month" and "Last Month" if disableMonthPresets is true).
 */
export const computeStaticRanges = (disableMonthPresets = true, customStaticRanges) => {
  if (customStaticRanges) {
    return customStaticRanges;
  }
  if (!disableMonthPresets) {
    return defaultStaticRanges;
  }
  return defaultStaticRanges.filter((staticRange) => {
    const label = staticRange.label ? staticRange.label.toLowerCase() : '';
    return !label.includes('month');
  });
};

/**
 * Clamps date range selection to date thresholds and maxAllowedSpanInDays span.
 */
export const clampSelectionToDateThresholdsAndMaxSpan = (selection, maxAllowedSpanInDays, maxDateThreshold, minDateThreshold) => {
  if (!selection.startDate || !selection.endDate) {
    return selection;
  }
  let startDate = new Date(selection.startDate);
  let endDate = new Date(selection.endDate);

  const isValidMinThreshold = minDateThreshold instanceof Date && !isNaN(minDateThreshold.getTime());
  if (isValidMinThreshold && startDate < minDateThreshold) {
    startDate = new Date(minDateThreshold);
  }

  if (maxAllowedSpanInDays) {
    const selectionSpanInMs = endDate.getTime() - startDate.getTime();
    const selectedDaysSpan = Math.round(selectionSpanInMs / (1000 * 3600 * 24));

    if (selectedDaysSpan > maxAllowedSpanInDays) {
      const cappedEndDate = new Date(startDate);
      cappedEndDate.setDate(startDate.getDate() + maxAllowedSpanInDays);

      return {
        ...selection,
        startDate,
        endDate: maxDateThreshold && cappedEndDate > maxDateThreshold ? new Date(maxDateThreshold) : cappedEndDate,
      };
    }
  }

  return {
    ...selection,
    startDate,
    endDate,
  };
};

export const clampSelectionToMaxDays = clampSelectionToDateThresholdsAndMaxSpan;
